import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { stripLocaleFromPath, getLocaleFromPath, DEFAULT_LOCALE } from "@/lib/locale";

const ORIGIN = process.env.REACT_APP_PUBLIC_ORIGIN || "https://depoia.com.br";

// Public seo string bank — kept out of the main i18n bundle for clarity.
const SEO_STRINGS = {
  pt: {
    home: {
      title: "depoia — Depoimentos em vídeo direto no seu site",
      desc: "Envie um link, o cliente grava sozinho pelo celular e o mural aparece no seu site. Sem edição, sem app. LGPD embutido.",
    },
    pricing: {
      title: "Preços — depoia",
      desc: "Planos flexíveis pra coletar depoimentos em vídeo, voz e texto. Comece grátis, faça upgrade quando quiser.",
    },
    integrations: {
      title: "Integrações — cole o depoia em qualquer site",
      desc: "Guias sem código pra WordPress, Shopify, Wix, Webflow, Framer e mais. Cole o widget em 30 segundos.",
    },
    privacy: {
      title: "Política de Privacidade — depoia",
      desc: "Como o depoia trata dados pessoais, conforme LGPD e GDPR.",
    },
    terms: {
      title: "Termos de Uso — depoia",
      desc: "Termos de uso da plataforma depoia.",
    },
    login: {
      title: "Entrar no depoia",
      desc: "Faça login com o Google para gerenciar suas campanhas e depoimentos.",
    },
  },
  en: {
    home: {
      title: "depoia — Video testimonials straight on your site",
      desc: "Send a link, your customer records on their phone, the wall appears on your site. No editing, no app. GDPR-ready.",
    },
    pricing: {
      title: "Pricing — depoia",
      desc: "Flexible plans to collect video, voice and text testimonials. Start free, upgrade anytime.",
    },
    integrations: {
      title: "Integrations — paste depoia on any site",
      desc: "No-code guides for WordPress, Shopify, Wix, Webflow, Framer and more. Paste the widget in 30 seconds.",
    },
    privacy: {
      title: "Privacy Policy — depoia",
      desc: "How depoia handles personal data, aligned with LGPD and GDPR.",
    },
    terms: {
      title: "Terms of Service — depoia",
      desc: "Terms of service for the depoia platform.",
    },
    login: {
      title: "Sign in to depoia",
      desc: "Sign in with Google to manage your campaigns and testimonials.",
    },
  },
};

export function SeoHead({ pageKey, title, description, ogImage, noindex = false }) {
  const { i18n } = useTranslation();
  const location = useLocation();
  const locale = getLocaleFromPath(location.pathname) || i18n.language?.slice(0, 2) || DEFAULT_LOCALE;
  const strings = (SEO_STRINGS[locale] || SEO_STRINGS.pt)[pageKey] || {};
  const finalTitle = title || strings.title || "depoia";
  const finalDesc = description || strings.desc || "";
  const finalOg = ogImage || `${ORIGIN}/og-image-${locale}.jpg`;

  const cleanPath = stripLocaleFromPath(location.pathname);
  const canonical = `${ORIGIN}/${locale}${cleanPath === "/" ? "" : cleanPath}`;
  const altPt = `${ORIGIN}/pt${cleanPath === "/" ? "" : cleanPath}`;
  const altEn = `${ORIGIN}/en${cleanPath === "/" ? "" : cleanPath}`;

  return (
    <Helmet>
      <html lang={locale === "pt" ? "pt-BR" : "en"} />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="pt-BR" href={altPt} />
      <link rel="alternate" hrefLang="en" href={altEn} />
      <link rel="alternate" hrefLang="x-default" href={altEn} />
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="depoia" />
      <meta property="og:locale" content={locale === "pt" ? "pt_BR" : "en_US"} />
      <meta property="og:locale:alternate" content={locale === "pt" ? "en_US" : "pt_BR"} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:image" content={finalOg} />
      <meta property="og:url" content={canonical} />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={finalOg} />
    </Helmet>
  );
}
